import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { MyPlanDetail } from "components/features/MyPlanDetails/MyPlanDetail";
import { deleteActionCardAsync, fetchMyPlanDetailAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { apiRequest } from "services/ServiceUtil";
import {
  GATEWAY_SERVICE_METHOD,
  LIBRARY_SERVICE_METHOD,
  NOTIFICATION_SERVICE_METHOD,
  USER_SERVICES_METHOD
} from "services/commonService";
import { abovePropUserView } from "utils/CommonConstants";
import { pageNames, recommendationAccessTypes } from "utils/Types";

const SLICE = "AppSlice";

interface AppState {
  innCode: string;
  selectedTab: number;
  searchSelectedTab: number;
  filters: any;
  selectedFilters: any;
  filterIds: string[];
  openSectionsList: {};
  selectedCards: any;
  loadPages: boolean;
  searchText: string;
  viewAsForAbovePropUser: string;
  cardsCompletedPrev: any;
  activeActionCard: any;
  userName: any;
  isCopyActionEnabled: boolean;
  searchActive: boolean;
  innCodes: any;
  innCodeFetching: boolean;
  filtersApplied: boolean;
  userRoles: any;
  editEmailLoader: boolean;
  selectedUser: string;
  loggedInUserDetails: {
    accessRoles: any;
    isCorp: boolean;
    isConsultant: boolean;
    isAbovePropertyUserFetched: boolean;
    isConsultantFetched: boolean;
    isAbovePropertyUser: boolean;
    isLeadership: boolean | null;
    permission: string;
    adminConsoleAccess: boolean;
    templatePlansAccess: boolean;
    uploadRecommendationAccess: string;
    uploadMetricAccess: string;
    manageFeedbackAccess: string;
  };
  impersonationDetails: {
    isImpersonated: boolean;
    userDetails: any;
    navigateToURL: string;
  };
  navSelectedDetails: {
    leftNavSelected: string;
  };
  previousRoute: string | null;
  currentRoute: string | null;
  libraryRecentSearch: any;
  tableHelperData: {
    noOfRows: number;
  };
}

const initialState: AppState = {
  loggedInUserDetails: {
    accessRoles: {},
    isCorp: false,
    isConsultant: false,
    isAbovePropertyUserFetched: false,
    isConsultantFetched: false,
    isAbovePropertyUser: false,
    isLeadership: null,
    permission: "",
    adminConsoleAccess: false,
    templatePlansAccess: false,
    uploadRecommendationAccess: recommendationAccessTypes.disabled,
    uploadMetricAccess: "Disabled",
    manageFeedbackAccess: ""
  },
  impersonationDetails: {
    isImpersonated: false,
    navigateToURL: "",
    userDetails: {}
  },
  navSelectedDetails: {
    leftNavSelected: ""
  },
  selectedTab: 0,
  searchSelectedTab: 0,
  filters: [],
  selectedFilters: [],
  filterIds: [],
  viewAsForAbovePropUser: abovePropUserView["AP"],
  innCode: "",
  searchText: "",
  loadPages: true,
  openSectionsList: {},
  selectedCards: { cardList: [], sectionId: "" },
  cardsCompletedPrev: -1,
  activeActionCard: {},
  userName: {},
  isCopyActionEnabled: false,
  searchActive: false,
  innCodes: [],
  innCodeFetching: true,
  filtersApplied: false,
  userRoles: { roles: [], filters: [] },
  editEmailLoader: false,
  selectedUser: "",
  previousRoute: "",
  currentRoute: "",
  libraryRecentSearch: [],
  tableHelperData: {
    noOfRows: 0
  }
};

export const getUserAccessPermissions = createAsyncThunk(`${SLICE}/getUserAccessPermissions`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.postCorporatePermissions, data, thunkApi)
);

export const fetchFilters = createAsyncThunk(`${SLICE}/fetchFilters`, (thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getPlans, {}, thunkApi, ["filters"])
);

export const submitUserLoggedInDate = createAsyncThunk(`${SLICE}/submitUserLoggedInDate`, (thunkApi: any) => {
  const data = {
    update_log_in_timestamp: true
  };
  return apiRequest(USER_SERVICES_METHOD.patchAssociatesMetadata, {}, thunkApi, [], data);
});

export const fetchLoggedInUserInfoAsync = createAsyncThunk(
  `${SLICE}/fetchLoggedInUserInfoAsync`,
  (data: any, thunkApi) => apiRequest(USER_SERVICES_METHOD.getAssociates, {}, thunkApi, [], data)
);

export const editPreferredEmail = createAsyncThunk(`${SLICE}/update_user_access`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.patchAssociates, data, thunkApi)
);

export const fetchInncodes = createAsyncThunk(`${SLICE}/fetchInncodes`, (data: string, thunkApi) =>
  apiRequest(LIBRARY_SERVICE_METHOD.getInncode, {}, thunkApi, [], { searchText: data })
);

export const fetchRoles = createAsyncThunk(`${SLICE}/fetchRoles`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.getRoles, {}, thunkApi)
);

export const editRoleSelection = createAsyncThunk(`${SLICE}/editRoleSelection`, (data: any, thunkApi) =>
  apiRequest(USER_SERVICES_METHOD.postAssociatesRoles, data, thunkApi)
);

export const errorLogs = createAsyncThunk(`${SLICE}/errorLogs`, (data: any, thunkApi) => {
  apiRequest(GATEWAY_SERVICE_METHOD.postErrorLog, data, thunkApi);
});

export const postUserVisit = createAsyncThunk(`${SLICE}/postUserVisit`, (data: any, thunkApi) => {
  return apiRequest(NOTIFICATION_SERVICE_METHOD.postUserVisit, data, thunkApi);
});

export const AppSlice = createSlice({
  name: SLICE,
  initialState,
  reducers: {
    setImpersonationDetails: (state, action) => {
      state.loggedInUserDetails.isAbovePropertyUser = action.payload.userDetails.isAbovePropertyUser;
      state.loggedInUserDetails.isLeadership = action.payload.userDetails.roleCategory === "Leadership";
      state.loggedInUserDetails.isCorp = !action.payload.isImpersonated;
      state.loggedInUserDetails.isConsultant = action.payload.isImpersonated
        ? !action.payload.isImpersonated
        : state.userName.isConsultant;
      state.impersonationDetails = action.payload;
    },
    setCardsCompletedPrev: (state, action) => {
      state.cardsCompletedPrev = action.payload;
    },
    setViewAsForAbovePropUser: (state, action) => {
      state.viewAsForAbovePropUser = action.payload;
    },
    librarySelectedTab: (state, action) => {
      if (action.payload === pageNames.allPlans || pageNames.bookmarked) {
        state.searchActive = false;
      }
      state.selectedTab = action.payload;
      state.selectedFilters = [];
      state.filterIds = [];
      state.selectedUser = "";
      state.searchText = "";
      state.filtersApplied = false;
    },
    setFiltersApplied: (state, action) => {
      state.filtersApplied = action.payload;
    },
    searchSelectedTab: (state, action) => {
      state.searchSelectedTab = action.payload;
    },
    setSelectedFilters: (state, action) => {
      state.selectedFilters = action.payload;
      state.filterIds = action.payload.map((item: any) => item.checked).flat();
      if (state.filterIds.length > 0) {
        state.filtersApplied = true;
      }
    },
    clearSelectedFilters: (state) => {
      state.selectedFilters = [];
      state.filterIds = [];
    },
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },
    setInnCode: (state, action) => {
      state.innCode = action.payload;
    },
    setAdmin: (state, action) => {
      state.loggedInUserDetails.isCorp = action.payload;
      state.loggedInUserDetails.isConsultantFetched = !action.payload;
    },
    setAbovePropertyUser: (state, action) => {
      state.loggedInUserDetails.isAbovePropertyUser = action.payload;
      state.loggedInUserDetails.isAbovePropertyUserFetched = true;
    },
    setCopyActionPopupEnabled: (state, action) => {
      state.isCopyActionEnabled = action.payload;
    },
    setLoadPages: (state, action) => {
      state.loadPages = action.payload;
    },
    clearOpenSections: (state) => {
      state.openSectionsList = {};
    },
    setOpenSections: (state, action) => {
      state.openSectionsList = { ...state.openSectionsList, ...action.payload };
    },
    setSelectedCards: (state, action) => {
      state.selectedCards.cardList =
        action?.payload.sectionId === state.selectedCards.sectionId
          ? [...(state.selectedCards?.cardList || []), action.payload.cardList]
          : [action.payload.cardList];
      state.selectedCards.sectionId = action?.payload.sectionId;
      state.selectedCards.planId = action?.payload.planId;
    },
    clearSelectedCards: (state, action) => {
      const dataArray = [...state.selectedCards.cardList];
      state.selectedCards.cardList = dataArray.filter((el) => el.id !== action.payload?.id);
    },
    setAllCards: (state, action) => {
      state.selectedCards.cardList = action?.payload.cardList;
      state.selectedCards.sectionId = action?.payload.sectionId;
      state.selectedCards.planId = action?.payload.planId;
    },
    clearAllCardSelection: (state) => {
      state.selectedCards.cardList = [];
      state.selectedCards.sectionId = "";
    },
    setActiveActionCard: (state, action) => {
      state.activeActionCard = action.payload;
    },
    setSearchActive: (state, action) => {
      state.searchActive = action.payload;
    },
    setInncodes: (state, action) => {
      state.innCodes = action.payload;
    },
    setSelectedUsers: (state, action) => {
      state.selectedUser = action.payload;
    },
    setSelectedLeftNav: (state, action) => {
      state.navSelectedDetails.leftNavSelected = action.payload;
    },
    setLoggedInUserInfo: (state, action) => {
      state.userName = action.payload?.length ? action.payload[0] : {};
      state.loggedInUserDetails.isLeadership = action.payload?.[0].roleCategory === "Leadership";
    },
    setpreviousRoute: (state, action) => {
      state.previousRoute = action.payload;
    },
    setCurrentRoute: (state, action) => {
      state.currentRoute = action.payload;
    },
    setLibraryRecentSearch: (state, action) => {
      const unique = action.payload.filter((item: any, index: number) => action.payload.indexOf(item) === index);
      state.libraryRecentSearch = unique;
    },
    setTableHelperData: (state, action) => {
      state.tableHelperData.noOfRows = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUserAccessPermissions.fulfilled, (state, action: any) => {
        const userDetails = action.payload.users[0];
        state.loggedInUserDetails = {
          ...state.loggedInUserDetails,
          ...userDetails,
          adminConsoleAccess: userDetails.adminConsoleAccess === "Y",
          uploadRecommendationAccess: userDetails.uploadRecommendationAccess,
          templatePlansAccess: userDetails.templatePlansAccess === "Enabled",
          isConsultant: userDetails.userType === "Consultant",
          isConsultantFetched: true
        };
      })
      .addCase(fetchFilters.fulfilled, (state, action: any) => {
        state.filters = Object.values(action?.payload).filter((value) => value !== 200);
      })
      .addCase(fetchLoggedInUserInfoAsync.fulfilled, (state, action: any) => {
        if (action.meta?.arg?.isImpersonated) {
          state.impersonationDetails.userDetails = action.payload?.users?.length ? action.payload?.users[0] : {};
        } else {
          state.userName = action.payload?.users?.length ? action.payload?.users[0] : {};
        }
        state.loggedInUserDetails.isLeadership = action.payload?.users[0].roleCategory === "Leadership";
      })
      .addCase(editPreferredEmail.pending, (state, action: any) => {
        state.editEmailLoader = true;
      })
      .addCase(editPreferredEmail.fulfilled, (state, action: any) => {
        state.editEmailLoader = false;
        if (state.impersonationDetails.isImpersonated) {
          state.impersonationDetails.userDetails = {
            ...state.impersonationDetails.userDetails,
            ...action.payload
          };
        } else {
          state.userName = { ...state.userName, ...action.payload };
        }
      })
      .addCase(editPreferredEmail.rejected, (state, action: any) => {
        state.editEmailLoader = false;
      })
      .addCase(deleteActionCardAsync.fulfilled, (state) => {
        state.selectedCards.cardList = [];
        state.selectedCards.sectionId = "";
      })
      .addCase(fetchMyPlanDetailAsync.fulfilled, (state, action: PayloadAction<MyPlanDetail>) => {
        state.cardsCompletedPrev = action.payload.cardsCompleted;
      })
      .addCase(fetchInncodes.pending, (state, action) => {
        state.innCodeFetching = true;
      })
      .addCase(fetchInncodes.fulfilled, (state, action) => {
        state.innCodes = action.payload.properties || [];
        state.innCodeFetching = false;
      })
      .addCase(fetchInncodes.rejected, (state, action) => {
        state.innCodeFetching = false;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.userRoles = action.payload;
      })
      .addCase(editRoleSelection.pending, (state, action) => {
        state.editEmailLoader = true;
      })
      .addCase(editRoleSelection.fulfilled, (state, action) => {
        const response = action.payload;
        state.editEmailLoader = false;
        if (state.impersonationDetails.isImpersonated) {
          state.impersonationDetails.userDetails = { ...state.impersonationDetails.userDetails, ...response };
        } else {
          state.userName = { ...state.userName, ...response };
        }
        state.loggedInUserDetails.isLeadership = response.roleCategory === "Leadership";
      })
      .addCase(editRoleSelection.rejected, (state, action) => {
        state.editEmailLoader = false;
      });
  }
});

export const {
  setImpersonationDetails,
  setCardsCompletedPrev,
  librarySelectedTab,
  setFiltersApplied,
  searchSelectedTab,
  setSelectedFilters,
  clearSelectedFilters,
  setCopyActionPopupEnabled,
  setInnCode,
  setSearchText,
  setAdmin,
  setLoadPages,
  clearOpenSections,
  setOpenSections,
  setSelectedCards,
  clearSelectedCards,
  setAllCards,
  clearAllCardSelection,
  setActiveActionCard,
  setSearchActive,
  setInncodes,
  setSelectedUsers,
  setAbovePropertyUser,
  setSelectedLeftNav,
  setLoggedInUserInfo,
  setViewAsForAbovePropUser,
  setCurrentRoute,
  setpreviousRoute,
  setLibraryRecentSearch,
  setTableHelperData
} = AppSlice.actions;

export default AppSlice.reducer;
